import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { isEqual } from 'lodash';
import { Subscription } from 'rxjs';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { MetricConversionType } from 'src/app/reusables/sol-range-slider-wrapper/models/range-slider-model';
import { ModuleManagerMetricInfo } from 'src/shared/models/module-manager/module-manager-settings.models';
import { RangeSliderData, SliderBound, ThresholdErrorTypes, ThresholdItem } from './widget-thresholds-modal.model';
import { WidgetThresholdsModalService } from './widget-thresholds-modal.service';

@Component({
    selector: 'cv-widget-thresholds-modal',
    templateUrl: './widget-thresholds-modal.component.html',
    styleUrls: ['./widget-thresholds-modal.component.scss']
})
export class WidgetThresholdsModalComponent implements OnInit, OnDestroy {
    @Input() moduleKey = '';
    @Input() metrics: ModuleManagerMetricInfo[] = [];

    selectedMetrics: number[] = [];
    previouslySelectedMetrics: number[] = [];
    metricNames = new Map<string, string>();
    metricThresholds: Record<string, ThresholdItem[]> = {};
    isLoading = false;
    selectorOpen = false;
    public formInvalid = false;
    public editMessage = "";

    savePromise: Promise<any> = null;
    subscriptions: Subscription[] = [];
    _openPromise: Promise<void> = null;
    _resolveOpenPromise: () => void = null;

    metricThresholdRangeMap: Record<number, RangeSliderData> = {};
    public hasThresholdError = false;
    private readonly hoursToSeconds = 3600;
    private readonly minutesToSeconds = 60;

    constructor(
        private activeModal: NgbActiveModal,
        private service: WidgetThresholdsModalService,
        private cvLocalizationService: CVLocalizationService,
        public changeDetector: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync(): Promise<void> {
        this.isLoading = true;
        await this.initiateLocalization();
        await this.initializeThresholdData();
        this.isLoading = false;
    }

    ngOnDestroy() {
        this._resolveOpenPromise?.();
        this._resolveOpenPromise = null;
        this._openPromise = null;
    }

    cancel() {
        this.activeModal.dismiss();
    }

    save() {
        if (this._openPromise !== null) {
            this._openPromise.then(() => {
                this.prepareForSave();
                this.service.saveWidgetThresholds(this.moduleKey, this.metricThresholds);
                this.activeModal.close();
            });
        } else {
            this.prepareForSave();
            this.service.saveWidgetThresholds(this.moduleKey, this.metricThresholds);
            this.activeModal.close();
        }
    }

    prepareForSave() {
        for (const metricId of this.selectedMetrics) {
            const currentMetricThreshold = this.metricThresholds[metricId];
            const metric = this.metrics.find(m => m.Id === metricId);
            currentMetricThreshold.forEach(threshold => {
                threshold.left = this.prepareValueForSave(threshold.left, metric);
                threshold.right = this.prepareValueForSave(threshold.right, metric);
            });
            this.metricThresholds[metricId] = currentMetricThreshold;
        }
    }

    prepareValueForSave(value: string | number, metric: ModuleManagerMetricInfo): number {
        if (typeof value === 'number' && metric?.ConversionType === MetricConversionType.Minutes) {
            return value / this.minutesToSeconds;
        } else if (typeof value === 'number' && metric?.ConversionType === MetricConversionType.Hours) {
            return value / this.hoursToSeconds;
        } else if (typeof value === 'string') {
            return parseFloat(value);
        } else {
            return value;
        }
    }

    applyThresholdFormatting() {
        for (const metricId of this.selectedMetrics) {
            if (this.getConversionType(metricId) === MetricConversionType.Time) {
                this.metricThresholds[metricId].forEach(threshold => {
                    threshold.left = this.formatTimeTypeValue(threshold.left as number);
                    threshold.right = this.formatTimeTypeValue(threshold.right as number);
                });
            }
        }
    }

    formatTimeTypeValue(value: number): string {
        let time = value;
        time = (time / 3600);
        const hours = Math.floor(time);
        time = ((time - hours) * (60));
        const minutes = Math.floor(time);
        time = ((time - minutes) * (60));
        const seconds = Math.round(time);

        const padZero = (digit) => digit.toString().padStart(2, '0');

        return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
    }

    getConversionType(metricId: number) {
        const index = this.metrics.findIndex(m => m.Id === metricId);
        return this.metrics[index].ConversionType;
    }

    getDirection(metricId: number) {
        return this.metrics.find(m => m.Id === metricId).Direction;
    }

    deselectMetric(key: string) {
        const index = this.selectedMetrics.findIndex(m => m === parseInt(key, 10));
        this.selectedMetrics.splice(index, 1);
        this.selectedMetrics = this.selectedMetrics.slice();
        delete this.metricThresholds[key];
        delete this.metricThresholdRangeMap[key];
        this.hasThresholdError = Object.values(this.metricThresholdRangeMap).some(
            ranges => ranges.error?.isMinError || ranges.error?.isMaxError || ranges.error?.isConflictOverrideError
        );
        this.previouslySelectedMetrics = this.selectedMetrics;
    }

    onSelectionChanged(event) {
        this.selectedMetrics = event;
        this.sortSelectedMetrics();
        this.updateThresholdsList();
    }

    onSelectorOpened() {
        this._openPromise = new Promise(resolve => this._resolveOpenPromise = resolve);
    }

    onSelectorClosed() {
        this._resolveOpenPromise?.();
        this._resolveOpenPromise = null;
        this._openPromise = null;
    }

    sortSelectedMetrics() {
        this.selectedMetrics.sort((a, b) => {
            const aName = this.metricNames.get(a.toString());
            const bName = this.metricNames.get(b.toString());
            if (aName > bName) return 1;
            else if (aName < bName) return -1;
            else return 0;
        });
    }

    updateThresholdsList() {
        const metricsToAdd = this.selectedMetrics.filter(m => this.previouslySelectedMetrics.indexOf(m) === -1);
        const metricsToDelete = this.previouslySelectedMetrics.filter(m => this.selectedMetrics.indexOf(m) === -1);

        metricsToDelete.forEach(metric => {
            delete this.metricThresholds[metric];
            delete this.metricThresholdRangeMap[metric];
        });

        metricsToAdd.forEach(metric => {
            this.metricThresholds[metric] = this.populateNewThresholds(metric);
            this.metricThresholdRangeMap[metric] = this.computeThresholdData(metric)
        });

        this.previouslySelectedMetrics = this.selectedMetrics;
    }

    async initiateLocalization(): Promise<void> {
        await this.cvLocalizationService.GetSection("CXOne_Modal");
        this.editMessage = this.cvLocalizationService.getCachedText(
            "CXOne_Modal", "Thresholds_Edit_Message", "Cannot edit. Permissions required."
        );
    }

    async initializeThresholdData(): Promise<void> {
        this.metricThresholds = await this.service.loadWidgetThresholds(this.moduleKey) ?? {};
        this.modifyThresholdDataForHourAndMinuteMetrics();
        this.selectedMetrics = Object.keys(this.metricThresholds).map(v => parseInt(v, 10));
        this.selectedMetrics = this.selectedMetrics.filter(selected => this.metrics.find(metric => metric.Id === selected));
        this.metrics.forEach(metric => this.metricNames.set(metric.Id.toString(), metric.Name));
        for (const metricId of this.selectedMetrics) {
            this.metricThresholdRangeMap[metricId] = this.computeThresholdData(metricId);
        }
        this.previouslySelectedMetrics = this.selectedMetrics;
        this.sortSelectedMetrics();
        this.applyThresholdFormatting();
    }

    modifyThresholdDataForHourAndMinuteMetrics(): void {
        Object.keys(this.metricThresholds).forEach(metricIdStr => {
            const metricId = parseInt(metricIdStr, 10);
            const metric = this.metrics.find(m => m.Id === metricId);
            if (metric && metric.ConversionType === MetricConversionType.Hours) {
                this.convertThresholdsToSeconds(metricId, this.hoursToSeconds);
            } else if (metric && metric.ConversionType === MetricConversionType.Minutes) {
                this.convertThresholdsToSeconds(metricId, this.minutesToSeconds);
            }
        });
    }

    convertThresholdsToSeconds(metricId: number, conversionFactor: number): void {
        this.metricThresholds[metricId].forEach(range => {
            range.left = Number(range.left) * conversionFactor;
            range.right = Number(range.right) * conversionFactor;
        });
    }

    computeThresholdData(metricId: number): RangeSliderData {
        if (this.metricThresholds[metricId]) {
            const sliderBounds = this.getSliderBounds(metricId);
            return {
                min: sliderBounds.sliderMin,
                max: sliderBounds.sliderMax,
                error: { isMinError: false, isMaxError: false, isConflictOverrideError: false }
            };
        }
        return {
            min: 0,
            max: 100,
            error: { isMinError: false, isMaxError: false, isConflictOverrideError: false }
        };
    }

    populateNewThresholds(metricId: number): ThresholdItem[] {
        const type = this.metrics.find(m => m.Id === metricId).ConversionType;
        const thresholds = [
            { enabled: false, left: '0', right: '0' },
            { enabled: false, left: '0', right: '0' },
            { enabled: false, left: '0', right: '0' }
        ];

        for (let i = 0; i < 3; i++) {
            switch (type) {
                case MetricConversionType.Number:
                case MetricConversionType.Seconds:
                case MetricConversionType.Minutes:
                case MetricConversionType.Hours:
                    {
                        thresholds[i].left = '0';
                        thresholds[i].right = '0';
                        break;
                    }
                case MetricConversionType.Time:
                    {
                        thresholds[i].left = '00:00:00';
                        thresholds[i].right = '00:00:00';
                        break;
                    }
                case MetricConversionType.Percentage:
                    {
                        thresholds[i].left = '0.00';
                        thresholds[i].right = '0.00';
                        break;
                    }
            }
        }
        return thresholds;
    }

    cleanTextInput($event, metric, thresholdIndex, level) {
        let cleanedValue = '';
        const type = this.metrics.find(m => m.Id === metric).ConversionType;
        switch (type) {
            case MetricConversionType.Number:
            case MetricConversionType.Percentage:
            case MetricConversionType.Seconds:
            case MetricConversionType.Minutes:
            case MetricConversionType.Hours:
                {
                    // remove multiple periods and limit to 2 decimal places
                    cleanedValue = $event.replace(/[^0-9.]/g, '');
                    const segments = cleanedValue.split('.');
                    if (segments.length > 2) {
                        cleanedValue = segments[0] + '.' + segments[1] + segments[2];
                    }
                    const periodIndex = cleanedValue.indexOf('.');
                    if (periodIndex > -1) {
                        cleanedValue = cleanedValue.slice(0, periodIndex + 3);
                    }
                    break;
                }
            case MetricConversionType.Time:
                {
                    cleanedValue = $event.replace(/[^0-9:]/g, '');
                    const segments = cleanedValue.split(':');
                    if (segments.length > 3) {
                        cleanedValue = segments[0] + ':' + segments[1] + ':' + segments[2];
                    }
                    break;
                }
        }

        if (level) {
            this.metricThresholds[metric][thresholdIndex].right = null;
            this.changeDetector.detectChanges();
            this.metricThresholds[metric][thresholdIndex].right = cleanedValue;
        }
        else {
            this.metricThresholds[metric][thresholdIndex].left = null;
            this.changeDetector.detectChanges();
            this.metricThresholds[metric][thresholdIndex].left = cleanedValue;
        }
    }

    private getSliderBounds(metricId: number): SliderBound {
        const conversionType = this.getConversionType(metricId);
        const timeBounds = { sliderMin: 0, sliderMax: 86400 }; // 24 hours in seconds
        const percentageBounds = { sliderMin: 0, sliderMax: 100 };

        // Group cases with the same bounds
        switch (conversionType) {
            case MetricConversionType.Percentage:
            case MetricConversionType.Number:
                return percentageBounds;

            case MetricConversionType.Seconds:
            case MetricConversionType.Minutes:
            case MetricConversionType.Hours:
            case MetricConversionType.Time:
                return timeBounds;

            default:
                return percentageBounds;
        }
    }

    onRangeDataCreated(ranges: ThresholdItem[], metricId: number) {
        if (metricId && !isEqual(ranges, this.metricThresholds[metricId])) {
            setTimeout(() => {
                this.metricThresholdRangeMap[metricId].error = { ...this.metricThresholdRangeMap[metricId].error, isConflictOverrideError: false };
                this.metricThresholds[metricId] = ranges;
                this.hasThresholdError = Object.values(this.metricThresholdRangeMap).some(
                    ranges => ranges.error?.isMinError || ranges.error?.isMaxError || ranges.error?.isConflictOverrideError
                );
            });
        }
    }

    onRangeError(errorObj: ThresholdErrorTypes, metricId: number) {
        this.metricThresholdRangeMap = {
            ...this.metricThresholdRangeMap,
            [metricId]: {
                ...this.metricThresholdRangeMap[metricId],
                error: { ...errorObj }
            }
        };

        this.hasThresholdError = Object.values(this.metricThresholdRangeMap).some(
            ranges => ranges.error?.isMinError || ranges.error?.isMaxError || ranges.error?.isConflictOverrideError
        );
    }

    trackByMetric(_: number, metricId: number): number {
        return metricId;
    }
}
